import { useEffect, useMemo, useRef, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Box, HStack, Pressable, VStack } from 'native-base';
import moment from 'moment';

import { Button, Text } from '@pimm/base';
import { snapToFiveMinutes } from '../_helper';
import { CalendarPicker } from './calendar-picker';
import { QuickPickPill } from './date-time-range-bar';
import { TimePicker } from './time-picker';

const MAX_DAYS = 30;

const formatLabel = (d: Date) => moment(d).format('MMM D, YYYY h:mm A');

const combineDateAndTime = (date: Date, time: Date) =>
  moment(date)
    .set({
      hour: moment(time).hour(),
      minute: moment(time).minute(),
      second: 0,
      millisecond: 0,
    })
    .toDate();

const isBetweenInclusive = (d: Date, a: Date, b: Date) => {
  const t = moment(d).startOf('day');
  const start = moment(a).startOf('day');
  const end = moment(b).startOf('day');
  return t.isSameOrAfter(start) && t.isSameOrBefore(end);
};

export type DateTimeRangePickerProps = {
  startDate: Date;
  endDate: Date;
  today: Date;
  onChange: (next: { startDate: Date; endDate: Date }) => void;
  // True when the active range doesn't match any of the toolbar's rolling
  // presets (Today / 3 / 7 / 30 days ending at now). When set, the
  // trigger paints itself with the primary.500 border so the operator can
  // see "you're on a custom range" at a glance — replacing the old
  // standalone "Custom" pill that used to surface beside the presets.
  isCustom?: boolean;
  // Optional ref to the chart panel container. When provided, the popover
  // positions itself with `position: fixed` and anchors its left + right
  // edges to the chart panel's bounding rect (with a small inset) — so
  // it stays inside the pane even when the natural ~720px content would
  // otherwise overflow leftward onto the list rail / site selector.
  panelRef?: React.MutableRefObject<any>;
};

export const DateTimeRangePicker = ({ startDate, endDate, today, onChange, isCustom, panelRef }: DateTimeRangePickerProps) => {
  const { t } = useTranslation();
  const { width: screenWidth } = useWindowDimensions();
  const [isOpen, setIsOpen] = useState(false);

  // Popover position computed from the chart panel's bounding rect when
  // available, so the popover stays inside the pane. Falls back to
  // null = use the legacy absolute-positioned layout (right-aligned to
  // the trigger) for callers that don't pass `panelRef`.
  const [popoverRect, setPopoverRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const [draftStart, setDraftStart] = useState<Date>(startDate);
  const [draftEnd, setDraftEnd] = useState<Date | undefined>(endDate);
  const [draftStartTime, setDraftStartTime] = useState<Date>(startDate);
  const [draftEndTime, setDraftEndTime] = useState<Date>(endDate);
  // Which quick-pick preset (if any) the current drafts came from. Set
  // when the user clicks a right-rail option, cleared the moment they
  // touch the calendar or time controls. Drives the "active" highlight
  // on the preset rail so the operator can see which option is in play.
  const [activePreset, setActivePreset] = useState<string | undefined>(undefined);
  const wrapperRef = useRef<any>(null);

  // Seed drafts each time the popover opens so we don't carry stale state
  // across opens.
  useEffect(() => {
    if (isOpen) {
      setDraftStart(startDate);
      setDraftEnd(endDate);
      setDraftStartTime(startDate);
      setDraftEndTime(endDate);
      setActivePreset(undefined);
    }
  }, [isOpen, startDate, endDate]);

  // Compute popover position / sizing from the trigger rect each time the
  // popover opens (and on window resize while open on web).
  // Web:    uses getBoundingClientRect + position:fixed anchored to panel bounds.
  // Native: uses measureInWindow to cap maxWidth within the screen right edge.
  useEffect(() => {
    if (!isOpen) return;
    const INSET = 8;

    if (Platform.OS === 'web') {
      const compute = () => {
        const panel = panelRef?.current;
        const trigger = wrapperRef.current as HTMLElement | null;
        if (!panel || !trigger) {
          setPopoverRect(null);
          return;
        }
        const panelRect = panel.getBoundingClientRect();
        const triggerRect = trigger.getBoundingClientRect();
        const left = triggerRect.left;
        const maxWidth = Math.max(280, panelRect.right - left - INSET);
        setPopoverRect({ top: triggerRect.bottom + 4, left, width: maxWidth });
      };
      compute();
      window.addEventListener('resize', compute);
      return () => window.removeEventListener('resize', compute);
    }

    // Native: measure trigger screen position to derive the available width
    // so the picker never overflows the right edge of the screen.
    const trigger = wrapperRef.current;
    if (!trigger?.measureInWindow) {
      setPopoverRect(null);
      return;
    }
    trigger.measureInWindow((x: number) => {
      setPopoverRect({ top: 0, left: 0, width: Math.max(280, screenWidth - x - INSET) });
    });
  }, [isOpen, panelRef, screenWidth]);

  // Click-outside to close — web only (document.mousedown is a DOM API).
  // On native, the popover closes via the Pressable toggle.
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      const node = wrapperRef.current as HTMLElement | null;
      if (node && e.target && !node.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // After a start is selected but no end yet, the right calendar caps at
  // start + 30 days so the user physically cannot pick beyond the window.
  const endMax = useMemo(() => {
    if (!draftStart) return undefined;
    return moment(draftStart).add(MAX_DAYS, 'days').toDate();
  }, [draftStart]);

  // 23:55 is the last 5-minute boundary of the day.
  const endOfDayLast5 = (d: Date) => moment(d).hour(23).minute(55).second(0).millisecond(0).toDate();

  // Right-rail quick-pick presets.
  const quickPresets = useMemo(
    () => [
      {
        id: 'today',
        label: t('store_equipment:today'),
        start: moment(today).startOf('day').toDate(),
        end: today,
      },
      {
        id: 'yesterday',
        label: t('store_equipment:yesterday'),
        start: moment(today).subtract(1, 'day').startOf('day').toDate(),
        end: endOfDayLast5(moment(today).subtract(1, 'day').toDate()),
      },
      {
        id: 'three_days',
        label: t('store_equipment:three_days'),
        start: moment(today).subtract(3, 'days').startOf('day').toDate(),
        end: today,
      },
      {
        id: 'seven_days',
        label: t('store_equipment:seven_days'),
        start: moment(today).subtract(7, 'days').startOf('day').toDate(),
        end: today,
      },
      {
        id: 'thirty_days',
        label: t('store_equipment:thirty_days'),
        start: moment(today).subtract(30, 'days').startOf('day').toDate(),
        end: today,
      },
    ],
    [today, t],
  );

  const handlePresetClick = (presetId: string) => {
    const preset = quickPresets.find(_ => _.id === presetId);
    if (!preset) return;
    setDraftStart(preset.start);
    setDraftEnd(preset.end);
    setDraftStartTime(preset.start);
    setDraftEndTime(preset.end);
    setActivePreset(preset.id);
  };

  const handleClickDay = (date: Date) => {
    // Any manual calendar interaction clears the active preset highlight.
    setActivePreset(undefined);

    // No end selected yet → second click decides end (forward) or restarts (backward).
    if (!draftEnd) {
      if (moment(date).isBefore(moment(draftStart), 'day')) {
        setDraftStart(date);
        setDraftEnd(undefined);
        return;
      }

      let nextEnd: Date;
      if (moment(date).diff(draftStart, 'days') > MAX_DAYS) {
        nextEnd = moment(draftStart).add(MAX_DAYS, 'days').toDate();
      } else {
        nextEnd = date;
      }
      setDraftEnd(nextEnd);

      if (moment(nextEnd).isBefore(moment(today), 'day')) {
        setDraftEndTime(endOfDayLast5(nextEnd));
      }
      return;
    }

    // Both already selected → start a fresh range.
    setDraftStart(date);
    setDraftEnd(undefined);
  };

  const renderRangeDay =
    (calendarMonth: Date) =>
    (day: Date, { isToday }: { isToday?: boolean; isActive: boolean }) => {
      const isPrimaryMonth = moment(day).isSame(calendarMonth, 'month');
      if (!isPrimaryMonth) {
        return (
          <Box w="full" h="full" alignItems="center" justifyContent="center">
            <Text size="md" color="gray.500" fontWeight={400}>
              {day.getDate()}
            </Text>
          </Box>
        );
      }

      const isStart = moment(day).isSame(draftStart, 'day');
      const isEnd = !!draftEnd && moment(day).isSame(draftEnd, 'day');
      const isInRange = draftEnd ? isBetweenInclusive(day, draftStart, draftEnd) : false;
      const isEndpoint = isStart || isEnd;
      const hasMultiDayRange = !!draftEnd && !moment(draftStart).isSame(draftEnd, 'day');
      const startConnects = isStart && hasMultiDayRange;
      const endConnects = isEnd && hasMultiDayRange;

      return (
        <Box w="full" h="full" position="relative" alignItems="center" justifyContent="center">
          {isInRange && !isEndpoint && <Box position="absolute" top={0} bottom={0} left={0} right={0} bgColor="primary.100" />}
          {startConnects && <Box position="absolute" top={0} bottom={0} left="50%" right={0} bgColor="primary.100" />}
          {endConnects && <Box position="absolute" top={0} bottom={0} left={0} right="50%" bgColor="primary.100" />}
          {isEndpoint && (
            <Box
              position="absolute"
              top={0}
              bottom={0}
              left={0}
              right={0}
              bgColor="primary.500"
              borderTopLeftRadius={endConnects ? 0 : 'md'}
              borderBottomLeftRadius={endConnects ? 0 : 'md'}
              borderTopRightRadius={startConnects ? 0 : 'md'}
              borderBottomRightRadius={startConnects ? 0 : 'md'}
            />
          )}
          <Text size="md" color={isEndpoint ? 'white' : isToday ? 'primary.500' : 'black'} fontWeight={isEndpoint ? 600 : 400} zIndex={1}>
            {day.getDate()}
          </Text>
        </Box>
      );
    };

  const canApply = !!draftStart && !!draftEnd;

  const handleApply = () => {
    if (!draftStart || !draftEnd) return;
    // Snap to 5-minute boundaries — temperature data is bucketed at 5-min.
    const startMs = snapToFiveMinutes(combineDateAndTime(draftStart, draftStartTime).getTime());
    const endMs = snapToFiveMinutes(combineDateAndTime(draftEnd, draftEndTime).getTime());
    onChange({ startDate: new Date(startMs), endDate: new Date(endMs) });
    setIsOpen(false);
  };

  const handleCancel = () => setIsOpen(false);

  const topCalendarDate = draftStart;
  const bottomCalendarDate = useMemo(() => moment(draftStart).add(1, 'month').toDate(), [draftStart]);
  const calendarMax = useMemo(() => {
    if (!draftStart || draftEnd) return today;
    return endMax && endMax < today ? endMax : today;
  }, [draftStart, draftEnd, endMax, today]);

  // Shared picker content — rendered inside the web popover or the native Modal.
  const pickerCards = (
    <HStack alignItems="stretch" px={2} pt={2} pb={2} space={2}>
      {/* Card 1 — stacked calendars */}
      <VStack bg="white" rounded="lg" shadow={1} px={2} py={2} space={0}>
        <CalendarPicker
          key={`top-${moment(topCalendarDate).format('YYYY-MM')}`}
          date={topCalendarDate}
          today={today}
          max={calendarMax}
          onChange={handleClickDay}
          renderDay={renderRangeDay(topCalendarDate)}
        />
        <CalendarPicker
          key={`bottom-${moment(bottomCalendarDate).format('YYYY-MM')}-${moment(draftStart).format('YYYY-MM-DD')}`}
          date={bottomCalendarDate}
          today={today}
          max={calendarMax}
          onChange={handleClickDay}
          renderDay={renderRangeDay(bottomCalendarDate)}
        />
      </VStack>

      {/* Cards 2 + 3 — Start Time and End Time */}
      <VStack space={2} alignSelf="stretch" minW={240}>
        <VStack flex={1} space={1} bg="white" rounded="lg" shadow={1} px={3} pt={3} pb={3} justifyContent="center">
          <Text size="lg" color="black" fontWeight={600} lineHeight="lg">
            {t('store_equipment:start_time')}
          </Text>
          <Text size="md" color="gray.600" fontWeight={500} lineHeight="md">
            {moment(draftStart).format('MMM D, YYYY')}
          </Text>
          <TimePicker
            value={draftStartTime}
            onChange={d => {
              setDraftStartTime(d);
              setActivePreset(undefined);
            }}
          />
        </VStack>

        <VStack flex={1} space={1} bg="white" rounded="lg" shadow={1} px={3} pt={3} pb={3} justifyContent="center">
          <Text size="lg" color="black" fontWeight={600} lineHeight="lg">
            {t('store_equipment:end_time')}
          </Text>
          <Text size="md" color={draftEnd ? 'gray.600' : 'gray.400'} fontWeight={500} lineHeight="md">
            {draftEnd ? moment(draftEnd).format('MMM D, YYYY') : t('store_equipment:end_date_placeholder')}
          </Text>
          <TimePicker
            value={draftEndTime}
            onChange={d => {
              setDraftEndTime(d);
              setActivePreset(undefined);
            }}
            disabled={!draftEnd}
          />
        </VStack>
      </VStack>

      {/* Card 4 — quick-pick rail + action buttons */}
      <VStack space={2} minW={130} bg="white" rounded="lg" shadow={1} alignSelf="stretch" px={2} py={3} justifyContent="space-between">
        <VStack space={2}>
          {quickPresets.map(_preset => (
            <QuickPickPill
              key={_preset.id}
              label={_preset.label}
              active={activePreset === _preset.id}
              onPress={() => handlePresetClick(_preset.id)}
            />
          ))}
        </VStack>
        <VStack space={2} pt={2}>
          <Button size="md" variant="unstyled" borderWidth={1} borderColor="gray.300" bg="white" shadow={0} onPress={handleCancel}>
            <Text size="md" fontWeight={600} color="gray.900">
              {t('app:cancel')}
            </Text>
          </Button>
          <Button size="md" shadow={0} isDisabled={!canApply} onPress={handleApply}>
            <Text size="md" fontWeight={600} color="black">
              {t('app:apply')}
            </Text>
          </Button>
        </VStack>
      </VStack>
    </HStack>
  );

  return (
    <Box ref={wrapperRef} position="relative" zIndex={10}>
      <Pressable onPress={() => setIsOpen(prev => !prev)} accessibilityRole="button">
        <HStack
          space={2}
          px={3}
          py={2}
          borderWidth={1}
          borderColor={isOpen || isCustom ? 'primary.500' : 'gray.300'}
          borderRadius="lg"
          bg="white"
          alignItems="center"
        >
          <Text size="md" color="black" fontWeight={600} lineHeight="sm">
            {formatLabel(startDate)}
          </Text>
          <Text size="md" color="gray.500" fontWeight={500}>
            →
          </Text>
          <Text size="md" color="black" fontWeight={600} lineHeight="sm">
            {formatLabel(endDate)}
          </Text>
        </HStack>
      </Pressable>

      {/* Dropdown anchored to the trigger — web: fixed inside chart panel; native: absolute below trigger */}
      {isOpen && (
        <Box
          backgroundColor="gray.100"
          borderWidth={2}
          borderColor="gray.400"
          borderRadius="lg"
          shadow={5}
          zIndex={9999}
          {...(Platform.OS === 'web'
            ? popoverRect
              ? {
                  position: 'absolute' as const,
                  style: {
                    opacity: 1,
                    position: 'fixed',
                    top: popoverRect.top,
                    left: popoverRect.left,
                    maxWidth: popoverRect.width,
                  } as any,
                }
              : {
                  position: 'absolute' as const,
                  top: '100%',
                  right: 0,
                  mt: 1,
                  style: { opacity: 1 },
                }
            : {
                // Native: opens below the trigger, left-aligned, width capped to screen
                position: 'absolute' as const,
                top: '100%',
                mt: -20,
                ...(popoverRect ? { maxWidth: popoverRect.width } : {}),
              })}
        >
          {pickerCards}
        </Box>
      )}
    </Box>
  );
};
